import { ArrowUpRight, Quotes } from "@phosphor-icons/react";

/**
 * "Vu dans la presse" band shown on the Landing page.
 *
 * The `articles` array below is the single source of truth: to add a new press
 * mention, just push a new object here. Layout adapts automatically.
 */
const articles = [
  {
    id: "ladepeche-2026-08-29",
    outlet: "La Dépêche du Midi",
    outletShort: "La Dépêche",
    outletColor: "#a80028", // brand burgundy of La Dépêche
    date: "29 août 2026",
    author: "Tifenn Richignac",
    title:
      "« C'est l'angoisse de tous les parents », une Toulousaine invente un site gratuit pour éviter la panique et retrouver les doudous perdus des enfants",
    quote:
      "J'aimerais que l'utilisation de ce site devienne un véritable réflexe.",
    section: "Grand Toulouse · Famille",
    image:
      "https://images.ladepeche.fr/api/v1/images/view/6a745dd300965a0f0801babf/large/image.jpg?v=1",
    url: "https://www.ladepeche.fr/2026/08/29/cest-langoisse-de-tous-les-parents-une-toulousaine-invente-un-site-gratuit-pour-eviter-la-panique-et-retrouver-les-doudous-perdus-des-enfants-13499161.php",
  },
];

export default function PressBand() {
  if (!articles.length) return null;

  return (
    <section
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14"
      aria-labelledby="press-band-title"
      data-testid="press-band"
    >
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-700 font-bold text-xs tracking-[0.25em] uppercase">
          Vu dans la presse
        </span>
        <h2
          id="press-band-title"
          className="font-display text-3xl md:text-4xl font-black text-stone-800 mt-4"
        >
          Ils parlent de <span className="text-orange-500">Doudou Alert</span>
        </h2>
      </div>

      <div className="grid gap-6 md:gap-8">
        {articles.map((a) => (
          <a
            key={a.id}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group block rounded-3xl bg-white border border-rose-100 hover:border-orange-200 hover:shadow-xl hover:shadow-orange-100 transition-all overflow-hidden"
            data-testid={`press-article-${a.id}`}
          >
            <article className="grid md:grid-cols-[220px_1fr] gap-0">
              {/* Left: photo */}
              <div className="relative h-48 md:h-full bg-stone-100 overflow-hidden">
                <img
                  src={a.image}
                  alt={`Article ${a.outlet} — ${a.title}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 via-transparent" />
                <span
                  className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-white font-bold text-[11px] tracking-widest uppercase shadow-sm"
                  style={{ color: a.outletColor }}
                >
                  {a.outletShort}
                </span>
              </div>

              {/* Right: content */}
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 text-xs font-semibold text-stone-500 mb-4">
                  <span
                    className="px-2 py-1 rounded-md text-white font-bold text-[10px] tracking-widest uppercase"
                    style={{ backgroundColor: a.outletColor }}
                  >
                    {a.outlet}
                  </span>
                  <span>·</span>
                  <span>{a.date}</span>
                  <span className="hidden sm:inline">·</span>
                  <span className="hidden sm:inline text-stone-400">
                    {a.section}
                  </span>
                </div>

                <blockquote className="relative">
                  <Quotes
                    size={28}
                    weight="fill"
                    className="text-orange-300 absolute -left-1 -top-1"
                  />
                  <p className="pl-9 font-display text-xl md:text-2xl font-black text-stone-800 leading-snug">
                    {a.quote}
                  </p>
                </blockquote>

                <p className="mt-4 text-stone-600 leading-relaxed line-clamp-2">
                  {a.title}
                </p>

                <div className="mt-5 flex items-center gap-2 font-bold text-orange-600 group-hover:text-orange-700">
                  <span>Lire l'article sur {a.outletShort}</span>
                  <ArrowUpRight
                    size={18}
                    weight="bold"
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </div>
              </div>
            </article>
          </a>
        ))}
      </div>
    </section>
  );
}
